import React, { useState } from 'react';
import { X, Bell, CheckCircle, AlertCircle, Info } from 'lucide-react';

const NotificationPanel = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'New Order Received',
      message: 'You have received a new order for Product A',
      type: 'success',
      time: '5 minutes ago',
      read: false,
    },
    {
      id: 2,
      title: 'Inventory Alert',
      message: 'Product B is running low on stock',
      type: 'warning',
      time: '2 hours ago',
      read: false,
    },
    {
      id: 3,
      title: 'Payment Received',
      message: 'Payment of $1,500 has been processed',
      type: 'success',
      time: '1 day ago',
      read: true,
    },
    {
      id: 4,
      title: 'System Update',
      message: 'Scheduled maintenance on Saturday 10 PM',
      type: 'info',
      time: '2 days ago',
      read: true,
    },
  ]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-100';
      case 'warning':
        return 'bg-yellow-50 border-yellow-100';
      case 'error':
        return 'bg-red-50 border-red-100';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-100';
    }
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(notif => ({ ...notif, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(notif => notif.id !== id));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className={`fixed inset-y-0 right-0 w-96 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
      isOpen ? 'translate-x-0' : 'translate-x-full'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-center space-x-3">
          <Bell className="w-6 h-6 text-gray-700" />
          <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 border-b bg-gray-50">
        <div className="flex justify-between items-center">
          <button
            onClick={markAllAsRead}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            disabled={unreadCount === 0}
          >
            Mark all as read
          </button>
          <span className="text-sm text-gray-500">
            {unreadCount} unread
          </span>
        </div>
      </div>

      {/* Notifications List */}
      <div className="h-[calc(100vh-140px)] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6">
            <Bell className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No notifications</p>
            <p className="text-gray-400 text-sm mt-2">You're all caught up!</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 rounded-lg border ${getTypeColor(notification.type)} ${
                  !notification.read ? 'border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getTypeIcon(notification.type)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">
                          {notification.title}
                        </h4>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mt-1">
                        {notification.message}
                      </p>
                      <p className="text-gray-400 text-xs mt-2">
                        {notification.time}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex space-x-3 mt-3 pt-3 border-t border-gray-100">
                  {!notification.read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Mark as read
                    </button>
                  )}
                  <button className="text-sm text-gray-600 hover:text-gray-800 font-medium">
                    View details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
        <div className="flex justify-between items-center">
          <button className="text-sm text-gray-600 hover:text-gray-900 font-medium">
            Notification settings
          </button>
          <button className="text-sm text-red-600 hover:text-red-800 font-medium">
            Clear all
          </button>
        </div>
      </div>
    </div>
  );
};

// Alternative: Simple version without icons dependency
export const SimpleNotificationPanel = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'New Order', message: 'Order #1234 received', time: '2 min ago', read: false },
    { id: 2, title: 'Low Stock', message: 'Product XYZ is low', time: '1 hour ago', read: false },
    { id: 3, title: 'Payment', message: 'Payment confirmed', time: '2 days ago', read: true },
  ]);

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  return (
    <div className={`fixed inset-y-0 right-0 w-80 bg-white shadow-xl z-50 transition-transform duration-300 ${
      isOpen ? 'translate-x-0' : 'translate-x-full'
    }`}>
      <div className="p-4 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Notifications</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>
      </div>
      
      <div className="p-2">
        <button 
          onClick={markAllAsRead}
          className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
        >
          Mark all as read
        </button>
      </div>

      <div className="overflow-y-auto h-[calc(100vh-120px)]">
        {notifications.map((notification) => (
          <div 
            key={notification.id} 
            className={`p-4 border-b hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : ''}`}
          >
            <div className="flex justify-between">
              <h4 className="font-medium">{notification.title}</h4>
              {!notification.read && (
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
            <p className="text-xs text-gray-400 mt-2">{notification.time}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationPanel;